import { useState, useMemo, useEffect } from 'react';
import { Filter } from '@increasecard/typed-components/dist/types/Filter';
import { dateToMonthAndYear, MonthAndYear } from './components/CalendarSection/useCalendarSection';
import { useShops } from '../../common/hooks/useShops';
import { useFilterOptions } from '../../common/hooks/useFilterOptions';
import { useCalendarData } from './useCalendarData';
import { useTranslation } from 'react-i18next';
import { compareOptionsByName } from '../../utils/filtersUtils';
import { Currency } from '@card/types';
import { useCurrency } from '@increasecard/increase-core';

export type Filters = Record<string, Filter>;

export function useDashboardScreen() {
  const { t } = useTranslation();
  const defaultCurrency = useCurrency();
  const [currency, setCurrency] = useState<Currency | undefined>(defaultCurrency || undefined);
  const [monthAndYear, setMonthAndYear] = useState<MonthAndYear>(dateToMonthAndYear(new Date()));
  const filterOptions = useFilterOptions();
  const [filters, setFilters] = useState<Filters>({});
  // TODO(santi698): Grab from API when endpoint is ready
  const lastUpdate = new Date('2020-06-11T19:03:00');
  const shops = useShops();
  const calendarDataOptions = useMemo(
    () => ({
      filters,
      monthAndYear,
      currency
    }),
    [filters, monthAndYear, currency]
  );
  const {
    soldAmounts,
    collectedAmounts,
    monthlyChargebacks,
    collectToday,
    collectTomorrow
  } = useCalendarData(calendarDataOptions);

  useEffect(() => {
    if (!filterOptions) return;
    setCurrency(filterOptions.currencies[0]);
  }, [filterOptions]);

  return {
    currencies: filterOptions?.currencies,
    currency,
    setCurrency,
    monthAndYear,
    setMonthAndYear,
    lastUpdate,
    filters,
    setFilters,
    soldAmounts,
    collectedAmounts,
    monthlyChargebacks,
    collectToday,
    collectTomorrow,
    filterFields: filterOptions && [
      {
        displayName: t('common:filters:shops:displayName'),
        name: 'shop_ids',
        type: 'selectMultiple',
        options: shops
          ? shops.map((shop) => ({ name: shop.name, value: shop.id })).sort(compareOptionsByName)
          : [],
        hasSearch: true,
        hasClearButton: true
      },
      {
        displayName: t('common:filters:bank:displayName'),
        name: 'bank_ids',
        type: 'selectMultiple',
        options: filterOptions.banks
          .map(({ id, name }) => ({ value: id, name }))
          .sort(compareOptionsByName),
        hasSearch: true,
        hasClearButton: true
      },
      {
        displayName: t('common:filters:processor:displayName'),
        name: 'processor_name',
        type: 'selectMultiple',
        options: filterOptions.processorNames
          .map((id) => ({
            name: t(`common:processorNames:${id}`),
            value: id
          }))
          .sort(compareOptionsByName)
      },
      {
        displayName: t('common:filters:cardBrand:displayName'),
        name: 'card_brand',
        type: 'selectMultiple',
        options: filterOptions.cardBrands
          .map((id) => ({
            name: t(`common:cardBrands:${id}`),
            value: id
          }))
          .sort(compareOptionsByName)
      }
    ]
  };
}
